import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import cn from 'classnames';
import { toast } from 'sonner';

import { Breadcrumb } from '@shared/components/bootstrap/breadcrumb';
import { useURLSearchParams } from '@shared/hooks/use_url_search_params';
import { UserRole } from '@shared/types/user_role';

import { AuctionAccountBanner } from '@admin/components/account/auction_account_banner';
import { CustomersPanel as AccountCustomersPanel } from '@admin/components/account/customers_panel';
import { DeleteButton as AccountDeleteButton } from '@admin/components/account/delete_button';
import { NewOrderButtons } from '@admin/components/account/new_order_buttons';
import { AccountBillingTab } from '@admin/components/accounts/account_billing_tab';
import { Addresses as AccountAddresses } from '@admin/components/accounts/addresses';
import { Attachments as AccountAttachments } from '@admin/components/accounts/attachments';
import { ClaimsTable as AccountClaimsTable } from '@admin/components/accounts/claims_table';
import { PaymentPlansPanel as AccountPaymentPlansPanel } from '@admin/components/accounts/payment_plans/panel';
import {
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
  useConfirmation,
} from '@admin/components/confirmation';
import { CollapsiblePanel } from '@admin/components/helpers/collapsible_panel';
import { Permission } from '@admin/components/helpers/permission';
import { Roles } from '@admin/components/helpers/roles';
import { HistoriesPanel } from '@admin/components/histories/panel';
import { LogisticsPurchaseOrdersPanel } from '@admin/components/logistics/purchase_orders/panel';
import { SkuBulkUploadForm as LogisticsSkuBulkUploadForm } from '@admin/components/logistics/skus/bulk_upload/form';
import { LogisticsSkusPanel } from '@admin/components/logistics/skus/panel';
import type { Recipient } from '@admin/components/messages/types';
import { MessagesWidget } from '@admin/components/messages/widget';
import { NotesPanel } from '@admin/components/notes/panel';
import { NotificationsPanel } from '@admin/components/notifications/notifications_panel';
import { List as OrdersList } from '@admin/components/orders/list';
import { CustomerTicketPanel } from '@admin/components/shared/customer_tickets_panel';
import { ThirdPartyTransfersList } from '@admin/components/third_party_transfers/list';
import { ThirdPartyTransferNewButton } from '@admin/components/third_party_transfers/new_button';
import { TicketsPanel } from '@admin/components/tickets/panel';
import { CreateTicket as CreateTechSupportTicket } from '@admin/components/tickets/tech_support/create_ticket';
import { WarehouseTasksAccountPanel } from '@admin/components/warehouse_tasks/account_panel';
import { PERMISSIONS } from '@admin/config/permissions';
import { accountsURL } from '@admin/config/routes';
import { useAccountRequest } from '@admin/hooks/use_account_request';
import { useTitle } from '@admin/hooks/use_title';
import { CLAIMS, CUSTOMER_TICKET } from '@admin/libraries/access_control_catalog.generated';
import { History__Subject, NoteNotableEnum, OrderSubject, Ticket__TicketableEnum } from '@admin/schema';
import type { IAccount } from '@admin/types/account';
import { getPrimaryCustomer } from '@admin/utils/account_customers';
import { notifyAccountInvoicesRefresh, unsuspendAccount, type AccountShowRecord } from '@admin/utils/account_show_api';
import { protectionSubscriptions, storageSubscriptions } from '@admin/utils/account_subscriptions';

import { AccountOverviewPanel } from './overview_panel';

export type AccountShowTab =
  | 'Home'
  | 'Appointments'
  | 'Third-Party Transfers'
  | 'Billing'
  | 'Payment-Plans'
  | 'Tickets'
  | 'Claims'
  | 'Attachments'
  | 'Messages'
  | 'Logistics-SKU'
  | 'Logistics-PurchaseOrder';

const TAB_LINKS: Array<{
  tab: AccountShowTab;
  label: string;
  className: string;
  testId?: string;
  show?(account: IAccount): boolean;
}> = [
  { tab: 'Home', label: 'Home', className: 'home-link' },
  { tab: 'Appointments', label: 'Appointments', className: 'orders-link' },
  { tab: 'Third-Party Transfers', label: 'Third-Party Transfers', className: 'third-party-transfers-link' },
  { tab: 'Billing', label: 'Billing', className: 'billing-link' },
  { tab: 'Payment-Plans', label: 'Payment Plans', className: 'payment-plans-link' },
  { tab: 'Tickets', label: 'Tickets', className: 'tickets-link' },
  { tab: 'Claims', label: 'Claims', className: 'claims-link', show: () => PERMISSIONS.has('claims:read') },
  { tab: 'Attachments', label: 'Attachments', className: 'attachments-link' },
  { tab: 'Messages', label: 'Messages', className: 'messages-link', testId: 'messages-tab' },
  {
    tab: 'Logistics-SKU',
    label: 'SKUs',
    className: 'logistics-sku-link',
    show: (account) => !!account.third_party_logistics,
  },
  {
    tab: 'Logistics-PurchaseOrder',
    label: 'Purchase Orders',
    className: 'logistics-purchase-order-link',
    show: (account) => !!account.third_party_logistics,
  },
];

const scrollToElement = (elementId: string, delayMs = 0) => {
  window.setTimeout(() => {
    document.getElementById(elementId)?.scrollIntoView();
  }, delayMs);
};

export const AccountShow: React.FC = () => {
  const [params] = useURLSearchParams();
  const scroll = params.get('scroll');
  const { accountID } = useParams<{ accountID: string }>();
  const { confirmation, confirm } = useConfirmation();
  const { response, refetch } = useAccountRequest(accountID);
  const [tab, setTab] = useState<AccountShowTab>(() => (params.get('tab') as AccountShowTab) ?? 'Home');
  const account = response?.data;
  useTitle(account ? `Acct: ${account.customer.name}` : undefined);

  const primaryCustomer = account ? getPrimaryCustomer(account) : undefined;
  const storagePlans = account ? storageSubscriptions(account.subscriptions) : [];
  const protectionPlans = account ? protectionSubscriptions(account.subscriptions) : [];

  const refreshAccount = useCallback(async () => {
    try {
      await refetch();
      notifyAccountInvoicesRefresh();
    } catch {
      toast.error('We were unable to refresh this account. Please try again.');
    }
  }, [refetch]);

  useEffect(() => {
    if (!scroll) return;
    scrollToElement(scroll, 2000);
  }, [scroll]);

  useEffect(() => {
    const onAccountsShowRefresh = () => {
      refreshAccount();
    };

    const onBroadcast = (event: Event) => {
      const detail = (event as CustomEvent<{ name?: string; data?: { form?: string } }>).detail;
      if (detail?.name !== 'inlineform:saved') return;
      if (detail.data?.form === 'chargesNew' || detail.data?.form === 'creditsAdd') {
        refreshAccount();
        document.dispatchEvent(new CustomEvent('notes.refresh'));
      }
    };

    document.addEventListener('admin:accounts-show-refresh', onAccountsShowRefresh);
    document.addEventListener('broadcast', onBroadcast as EventListener);

    return () => {
      document.removeEventListener('admin:accounts-show-refresh', onAccountsShowRefresh);
      document.removeEventListener('broadcast', onBroadcast as EventListener);
    };
  }, [refreshAccount]);

  const onScrollToSubscriptions = useCallback(() => {
    setTab('Billing');
    scrollToElement('subscriptions-anchor');
  }, []);

  const onCanceled = useCallback(() => {
    refreshAccount();
    toast.info('Account Successfully Canceled!');
  }, [refreshAccount]);

  const onSuspended = useCallback(() => {
    refreshAccount();
    toast.info('Account Successfully Suspended!');
  }, [refreshAccount]);

  const onBanUpdated = useCallback((banned: boolean) => {
    toast.info(`Account Successfully ${banned ? 'Enabled' : 'Disabled'}!`);
  }, []);

  const onUnsuspend = async () => {
    if (!account) return;

    const response = await confirm({
      title: 'Activate Account?',
      description: `Are you sure you want to activate account ${account.number}?`,
    });
    if (response !== ConfirmationResponse.Confirm) return;

    try {
      await unsuspendAccount(accountID);
      await refreshAccount();
      toast.info('Account Successfully Activated!');
    } catch {
      toast.error('We were unable to activate this account. Please contact support.');
    }
  };

  if (!account) return null;

  return (
    <>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}

      <AuctionAccountBanner accountID={accountID} />

      <div className="text-overflow pad-top pad-btm">
        <h1 className="page-header text-overflow">
          <ol className="breadcrumb pull-left">
            <Breadcrumb.Item>
              <NavLink to={accountsURL()}>Accounts</NavLink>
            </Breadcrumb.Item>
            {account.id && primaryCustomer && (
              <Breadcrumb.Item active>
                #{account.number} ({primaryCustomer.name})
                {account.partner_managed && (
                  <span className="label label-info" style={{ marginLeft: 8 }}>
                    Partner-managed
                  </span>
                )}
              </Breadcrumb.Item>
            )}
          </ol>
        </h1>
        <Roles show={[UserRole.Admin, UserRole.L2Agent, UserRole.Manager]}>
          <div className="pull-right">
            <CreateTechSupportTicket
              ticketable={{
                id: accountID,
                type: Ticket__TicketableEnum.Account,
                displayName: account.customer.name,
              }}
            />
          </div>
        </Roles>
      </div>

      <div className="row">
        <div className="col-md-6">
          <AccountDeleteButton id={accountID} />
          <AccountCustomersPanel accountID={accountID} />
        </div>
        <div className="col-md-6">
          <AccountOverviewPanel
            account={account}
            storagePlans={storagePlans}
            protectionPlans={protectionPlans}
            onScrollToSubscriptions={onScrollToSubscriptions}
            onCanceled={onCanceled}
            onSuspended={onSuspended}
            onBanUpdated={onBanUpdated}
            onUnsuspend={onUnsuspend}
          />
        </div>
      </div>

      <div className="tab-base">
        <ul className="nav nav-tabs">
          {TAB_LINKS.filter((link) => !link.show || link.show(account)).map((link) => (
            <li key={link.tab} className={cn(link.className, { active: tab === link.tab })}>
              <a
                href="#"
                data-test-id={link.testId}
                onClick={(event) => {
                  event.preventDefault();
                  setTab(link.tab);
                }}
              >
                <span>{link.label}</span>
              </a>
            </li>
          ))}
        </ul>

        {tab === 'Home' && (
          <div id="home-tab-content">
            <AccountAddresses accountID={accountID} />
            <NotesPanel type={NoteNotableEnum.Account} id={accountID} />
          </div>
        )}

        {tab === 'Appointments' && (
          <div id="order-tab-content">
            <CollapsiblePanel
              panelId="order-panel"
              title="Appointments"
              footer={<NewOrderButtons accountID={accountID} />}
            >
              <OrdersList id={accountID} type={OrderSubject.Account} />
            </CollapsiblePanel>
          </div>
        )}

        {tab === 'Third-Party Transfers' && (
          <div id="third-party-transfers-tab-content">
            <CollapsiblePanel
              panelId="third-party-transfers-panel"
              title="Third-Party Transfers"
              footer={<ThirdPartyTransferNewButton accountID={accountID} />}
            >
              <ThirdPartyTransfersList accountID={accountID} />
            </CollapsiblePanel>
          </div>
        )}

        {tab === 'Billing' && (
          <div id="billing-tab-content">
            <AccountBillingTab
              accountID={accountID}
              thirdPartyLogistics={!!account.third_party_logistics}
              billingDateChangeable={!!account.billing_date_changeable}
              billingDateChangedOn={account.billing_date_changed_on}
              subscriptionBillingDay={account.subscription_billing_day}
              suspendable={!!account.is_suspendable}
              onSuspended={onSuspended}
            />
          </div>
        )}

        {tab === 'Payment-Plans' && <AccountPaymentPlansPanel accountID={accountID} />}

        {tab === 'Tickets' && (
          <>
            <Permission resource={CUSTOMER_TICKET.resource} action={CUSTOMER_TICKET.actions.read} denied={null}>
              <CustomerTicketPanel customerTicketableID={accountID} customerTicketableType="Account" />
            </Permission>
            <WarehouseTasksAccountPanel accountID={accountID} />
            <TicketsPanel ticketable={{ id: accountID, type: Ticket__TicketableEnum.Account }} />
          </>
        )}

        {tab === 'Claims' && (
          <Permission resource={CLAIMS.resource} action={CLAIMS.actions.read}>
            <div id="claims-tab-content">
              <div className="panel">
                <div className="panel-heading">
                  <h3 className="panel-title">Claims</h3>
                </div>
                <div className="panel-body">
                  <AccountClaimsTable accountID={accountID} />
                </div>
              </div>
            </div>
          </Permission>
        )}

        {tab === 'Attachments' && (
          <div id="attachments-tab-content">
            <div className="panel">
              <div className="panel-heading">
                <h3 className="panel-title">Attachments</h3>
              </div>
              <div className="panel-body">
                <AccountAttachments accountID={accountID} />
              </div>
            </div>
          </div>
        )}

        {tab === 'Messages' && (
          <div id="messages-tab-content">
            <div id="messages-holder">
              <MessagesWidget recipient={account as unknown as Recipient} recipientType="Account" />
            </div>
            <NotificationsPanel accountId={accountID} />
          </div>
        )}

        {tab === 'Logistics-SKU' && (
          <>
            <LogisticsSkusPanel accountID={accountID} />
            <LogisticsSkuBulkUploadForm accountID={accountID} />
          </>
        )}

        {tab === 'Logistics-PurchaseOrder' && <LogisticsPurchaseOrdersPanel accountID={accountID} />}
      </div>

      <HistoriesPanel type={History__Subject.Account} id={accountID} />
    </>
  );
};
